import React from 'react'
import { useAttempt, useGradeAttempt } from '@sphere/learning/data-access'
import { LoadingSpinner } from '@sphere/shared/ui/design-system'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import { AttemptHeader } from '../attempt-header'
import { ActivityGrader } from '../activity-grader'
import type { GradingResult } from '../types'
import styles from './AttemptGradeView.module.scss'

interface AttemptGradeViewProps {
  attemptId: string
  onSuccess?: () => void
}

export const AttemptGradeView: React.FC<AttemptGradeViewProps> = ({
  attemptId,
  onSuccess,
}) => {
  const navigate = useNavigate()
  const { data: attempt, isLoading, error } = useAttempt(attemptId)
  const gradeAttempt = useGradeAttempt()

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper}>
        <LoadingSpinner fullPage={false} text="Chargement..." />
      </div>
    )
  }

  if (error || !attempt) {
    return (
      <div className={styles.error}>
        Erreur lors du chargement de la tentative.
      </div>
    )
  }

  // Check if the attempt can be graded
  const canGrade = attempt.capabilities?.canGrade
  const activityType = attempt.activity?.type?.code
  const content = attempt.activity?.content

  if (!canGrade) {
    return (
      <div className={styles.error}>
        Cette tentative ne peut pas être notée.
      </div>
    )
  }

  const handleGrade = async (result: GradingResult) => {
    try {
      await gradeAttempt.mutateAsync({
        id: attemptId,
        data: {
          score: result.totalScore,
          maxScore: result.maxScore,
          passed: result.passed,
          feedback: result.feedback,
          grading: {
            scores: result.scores,
            gradedAt: new Date().toISOString(),
          },
        },
      })
      toast.success('Notation enregistrée avec succès !')

      if (onSuccess) {
        onSuccess()
      } else {
        navigate(`/learning/activities/${attempt.activityId}`)
      }
    } catch (err) {
      console.error(err)
      toast.error("Erreur lors de l'enregistrement de la notation")
    }
  }

  // Extract initial scores from existing grading if present
  const initialScores = (attempt.grading as any)?.scores

  return (
    <div className={styles.container}>
      <AttemptHeader
        title={attempt.activity?.title}
        description={`Notation de la tentative de ${attempt.user?.firstName + ' ' + attempt.user?.lastName || "l'élève"}`}
        status={attempt.status?.label}
        studentName={
          attempt.user?.firstName + ' ' + attempt.user?.lastName || ''
        }
      />
      <div className={styles.card}>
        <ActivityGrader
          activityType={activityType}
          content={content}
          userAnswers={attempt.userAnswers}
          onGrade={handleGrade}
          isSubmitting={gradeAttempt.isPending}
          initialScores={initialScores}
        />
      </div>
    </div>
  )
}
